import { Injectable } from "@angular/core";
import { IExternalReference, IItemQuote, IRootItemData, ISectionItem, ISequence } from "libraries/interfaces/ISearchResponse";
import { forkJoin, Observable, of, Subject } from "rxjs";
import { EItemAction, IItemPostRequest } from "libraries/interfaces/watabi/ISearchParameters";
import { WatabiResourceService } from "libraries/service/resources/watabi-resources/watabiResource.service";
import { IBlock } from "libraries/interfaces/IItemService";
import { INotification, NotificationType } from "libraries/interfaces/INotification";
import { Yii } from "libraries/interfaces/Yii";
import { ITypeBlocksAndSectionsInChapterConfig, QuoteConfigService } from "../quote-config/quote-config.service";
import { flatMap, map } from "rxjs/operators";
import { EHosts } from "libraries/enums/EHosts";
import { DateService } from "libraries/date/date.service";

export enum ESeverityAlert {
    low = 'low',
    moderate = 'moderate',
    high = 'high',
}

export interface IConsistencyAlert {
    active: boolean;
    severity: ESeverityAlert;
    timestamp?: Date;
}

export const quoteAlertMessage: { [key in ESeverityAlert]: string } = {
    high: Yii.t('wui-watabi-js', 'Les modifications effectuées sur le dossier peuvent entraîner un recalcul des prix de la grille associée.'),
    low: Yii.t('wui-watabi-js', `Les modifications effectuées sur le dossier peuvent être incohérentes avec des informations de la grille de prix, mais n'impactent ni le calcul du prix, ni les informations textuelles de cotation restituée.`),
    moderate: Yii.t('wui-watabi-js', `Les modifications effectuées sur le dossier peuvent entraîner une mise à jour des informations textuelles de la cotation restituée sans impact sur le calcul du prix.`)
};

@Injectable()
export class QuoteAlertService {
    private readonly _notifyAlert: Subject<IConsistencyAlert | null> = new Subject();

    constructor(
        private readonly watabiResourceService: WatabiResourceService,
        private readonly quoteConfigService: QuoteConfigService,
        private readonly dateService: DateService,
    ) {
    }

    public getNotifyAlert(): Observable<IConsistencyAlert | null> {
        return this._notifyAlert.asObservable();
    }

    public setNotifyAlert(consistencyAlert?: IConsistencyAlert): void {
        this._notifyAlert.next(consistencyAlert ?? null);
    }

    public setMultipleQuoteAlert(quotes: IItemQuote[], consistencyAlert?: IConsistencyAlert): Observable<IItemQuote[]> {
        const alertableQuotes: IItemQuote[] = getAlertableQuoteList(quotes, consistencyAlert);

        const updateQuotes: IItemPostRequest[] = alertableQuotes.map((quote: IItemQuote) => {
            return {
                ...this.setConsistencyAlertOnQuoteItem(quote, consistencyAlert),
                action: EItemAction.Update,
            };
        });

        return this.watabiResourceService.setMultipleItems(updateQuotes) as Observable<IItemQuote[]>;
    }

    public setQuoteAlert(quote: IItemQuote, consistencyAlert?: IConsistencyAlert): Observable<IItemQuote> {
        return this.watabiResourceService.setItem({
            ...this.setConsistencyAlertOnQuoteItem(quote, consistencyAlert),
            action: EItemAction.Update,
        }) as Observable<IItemQuote>;
    }

    public setMultipleQuoteAlertOnBlockCategoryChange(
        newCategory: string,
        lastCategory: string,
        blockIdVersion: number,
        bookingData: IRootItemData,
    ): Observable<IItemPostRequest[]> {
        const blockChanged: IBlock = getBlockChanged(bookingData, blockIdVersion);
        const quoteParentIdVersion: number = getQuoteIdFromBlock(bookingData, blockChanged.idItem);
        const updateQuotes: IItemQuote[] = getAlertableQuoteList(bookingData.quotes)
            .filter((quote: IItemQuote) => quoteParentIdVersion && quote.idVersion === quoteParentIdVersion || !quoteParentIdVersion);

        if (!updateQuotes.length) {
            return of([]);
        }

        return this.getQuotesBlockExludeConfigStream(updateQuotes).pipe(
            map((quotesBlockExcludeConfig: { [key: string]: string[] }): IItemPostRequest[] => {
                return this.buildConsistencyAlertUpdateRequests(updateQuotes, quotesBlockExcludeConfig, lastCategory, newCategory);
            }),
        );
    }

    public setMultipleQuoteAlertOnBlockChange(
        bookingData: IRootItemData,
        blockIdVersion: number,
        consistencyAlert?: IConsistencyAlert
    ): Observable<IItemQuote[]> {
        const blockChanged: IBlock = getBlockChanged(bookingData, blockIdVersion);
        const quoteParentIdVersion: number = getQuoteIdFromBlock(bookingData, blockChanged.idItem);
        const updateQuotes: IItemQuote[] = getAlertableQuoteList(bookingData.quotes, consistencyAlert)
            .filter((quote: IItemQuote) => quoteParentIdVersion && quote.idVersion === quoteParentIdVersion || !quoteParentIdVersion);

        return updateQuotes.length && blockChanged ?
            this.getQuotesBlockExludeConfigStream(updateQuotes)
                .pipe(
                    map((quotesBlockExcludeConfig: { [key: string]: string[] }): IItemPostRequest[] => {
                        return updateQuotes
                            .filter((quoteItem: IItemQuote) => {
                                const blockExludedCategories: string[] = quotesBlockExcludeConfig[quoteItem.idItem];
                                return !blockExludedCategories.some((category: string) => category.toLowerCase() === blockChanged.category.code.toLowerCase());
                            })
                            .map((quote: IItemQuote): IItemPostRequest => {
                                return {
                                    ...this.setConsistencyAlertOnQuoteItem(quote, consistencyAlert),
                                    action: EItemAction.Update,
                                };
                            });
                    }),
                    flatMap((quoteUpdateRequest: IItemPostRequest[]) => quoteUpdateRequest.length > 0 ?
                        this.watabiResourceService.setMultipleItems(quoteUpdateRequest) as Observable<IItemQuote[]> :
                        of([])
                    )
                )
            :
            of([]);
    }

    public transformAlertToNotification(consistencyAlert: IConsistencyAlert): INotification {
        const notification: INotification = {
            type: consistencyAlert && consistencyAlert.severity === ESeverityAlert.high ? NotificationType.Warning : NotificationType.Info,
        };

        if (consistencyAlert) {
            notification.active = consistencyAlert.active;
            notification.message = quoteAlertMessage[consistencyAlert.severity];
        }

        return notification;
    }

    private buildConsistencyAlertUpdateRequests(
        updateQuotes: IItemQuote[], quotesBlockExcludeConfig: { [p: string]: string[] }, lastCategory: string, newCategory: string
    ): IItemPostRequest[] {

        return updateQuotes.map((quoteItem: IItemQuote): IItemPostRequest => {
            const blockExludedCategories: string[] = quotesBlockExcludeConfig[quoteItem.idItem];

            // l'ancienne catégorie du bloc est permise sur la cotation et la catégorie choisie ne l'est pas
            // Ou
            // l'ancienne catégorie du bloc n'était pas permise sur la cotation alors que la catégorie choisie l'est
            const lastBlockCategoryExcluded: boolean =
                blockExludedCategories.some((category: string) => category.toLowerCase() === lastCategory.toLowerCase());
            const newBlockCategoryExcluded: boolean =
                blockExludedCategories.some((category: string) => category.toLowerCase() === newCategory.toLowerCase());

            if ((!lastBlockCategoryExcluded && newBlockCategoryExcluded)
                || (lastBlockCategoryExcluded && !newBlockCategoryExcluded)) {
                return {
                    ...this.setConsistencyAlertOnQuoteItem(quoteItem, { active: true, severity: ESeverityAlert.high }),
                    action: EItemAction.Update,
                };
            }

            // no low-severity alerts on editorial quotes
            if (quoteItem.externalReference.some((externalReference: IExternalReference) => externalReference.host === EHosts.QUOTATION)) {
                // L'ancienne catégorie est permise sur la cotation et la catégorie choisie l'est également
                return {
                    ...this.setConsistencyAlertOnQuoteItem(quoteItem, { active: true, severity: ESeverityAlert.low }),
                    action: EItemAction.Update,
                };
            }

            // no alert needed
            return null;

        }).filter((request: IItemPostRequest): boolean => request !== null);
    }

    private getQuotesBlockExludeConfigStream(quotes: IItemQuote[]): Observable<{ [key: string]: string[] }> {
        const quotesConfigSream: Array<Observable<ITypeBlocksAndSectionsInChapterConfig>> = quotes
            .map((quoteItem: IItemQuote) => this.quoteConfigService.getQuoteTypeBlocksAndSectionsInChapterConfig(quoteItem));
        return forkJoin(quotesConfigSream)
            .pipe(
                map((typeBlocksAndSectionsInChapterConfig: ITypeBlocksAndSectionsInChapterConfig[]) =>
                    typeBlocksAndSectionsInChapterConfig
                        .reduce((acc: { [key: string]: string[] },
                            chapterConfigItem: ITypeBlocksAndSectionsInChapterConfig,
                            index: number): { [key: string]: string[] } => {
                            const quoteIdItem: string = quotes[index].idItem.toString();
                            return {
                                ...acc,
                                [quoteIdItem]: chapterConfigItem?.exclude?.blockCategory ?? []
                            };
                        }, {})
                )
            );
    }

    private setTimestampDate(quote: IItemQuote): IItemQuote {
        if (quote.consistencyAlert) {
            quote.consistencyAlert.timestamp = this.dateService.getNow();
        }
        return quote;
    }

    private setConsistencyAlertOnQuoteItem(quote: IItemQuote, consistencyAlert?: IConsistencyAlert): IItemQuote {
        if (consistencyAlert && consistencyAlert.active) {
            const currentAlertSeverity: ESeverityAlert = quote.consistencyAlert ? quote.consistencyAlert.severity : null;
            const currentSeverityLevel: number = getSeverityAlertLevel(currentAlertSeverity);
            const changedSeverityLevel: number = getSeverityAlertLevel(consistencyAlert.severity);
            if (changedSeverityLevel > currentSeverityLevel) {
                quote.consistencyAlert = {
                    ...quote.consistencyAlert,
                    ...consistencyAlert,
                };
            }
        } else if (
            (!consistencyAlert || !consistencyAlert.active)
        ) {
            quote.consistencyAlert = null;
        }
        return this.setTimestampDate(quote);
    }
}

function getBlockChanged(bookingData: IRootItemData, blockIdVersion: number): IBlock {
    return getBlocks(bookingData)
        .find(({ idVersion }: IBlock) => idVersion === blockIdVersion);
}

function getQuoteIdFromBlock(bookingData: IRootItemData, blockIdItem: number): number {
    const allQuoteSections: ISectionItem[] = (bookingData.quotes || []).reduce(
        (acc: ISectionItem[], parent: IItemQuote) => [
            ...acc,
            ...(parent.sections || []),
        ], []
    );

    const sectionFound: ISectionItem = allQuoteSections
        .find((section: ISectionItem) => (section.blocks || []).some((block: IBlock) => block.idItem === blockIdItem));

    return sectionFound ? sectionFound.idVersionParent : null;
}

function getBlocks(bookingData: IRootItemData): IBlock[] {
    const allSequences: ISequence[] = (bookingData.sequences || []);
    const allQuoteSections: ISectionItem[] = (bookingData.quotes || []).reduce(
        (acc: ISectionItem[], parent: IItemQuote) => [
            ...acc,
            ...(parent.sections || []),
        ], []
    );

    return [...allSequences, ...allQuoteSections].reduce(
        (acc: IBlock[], sequence: ISequence | ISectionItem) => [
            ...acc,
            ...(sequence.blocks || [])
        ], []
    );
}

function getAlertableQuoteList(quotes?: IItemQuote[], consistencyAlert?: IConsistencyAlert): IItemQuote[] {

    const severity: ESeverityAlert = consistencyAlert ? consistencyAlert.severity : null;

    // medium and high-severity alerts are for all quotes
    // low-severity consistency alerts are for quotations only
    return (quotes || [])
        .filter((quote: IItemQuote) => severity !== ESeverityAlert.low
            || (quote.externalReference &&
                quote.externalReference.some((extRef: IExternalReference) => extRef.host === EHosts.QUOTATION)));
}

function getSeverityAlertLevel(severityAlert: ESeverityAlert): number {
    switch (severityAlert) {
        case ESeverityAlert.low:
            return 1;
        case ESeverityAlert.moderate:
            return 2;
        case ESeverityAlert.high:
            return 3;
        default:
            return 0;
    }
}
